import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import {
  Button,
  DatePicker,
  Input,
  InputNumber,
  Result,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
} from "antd";
import type { FilterDropdownProps } from "antd/es/table/interface";
import {
  FilterOutlined,
  SearchOutlined,
  ShoppingOutlined,
  StarFilled,
} from "@ant-design/icons";
import axios from "axios";
import type { Dayjs } from "dayjs";
import { usePermissions } from "../../utils/usePermissions";
import { isSuperAdmin } from "../../utils/permissions";
import { BASE_API_URL } from "../../utils/consts";

interface TokenOption {
  _id: string;
  name: string;
  locationId?: { _id: string; name: string } | null;
}

interface SetMenuLink {
  ProductId: number;
  Price: number;
}

interface HappyHour {
  DayOfWeek: number;
  TimeFrom: string;
  TimeTo: string;
  Price: number;
}

interface ModifierItem {
  Id: number;
  ProductId: number;
  ProductName: string;
  Price: number;
  Multiselect: boolean;
  DisablePrint: boolean;
  SortOrder: number;
}

interface ModifierGroup {
  Id: number;
  Title: string;
  Min: number;
  Max: number;
  SortOrder: number;
  Items: ModifierItem[];
}

interface ProductComponent {
  Id: number;
  ChildProductId: number;
  ChildProductCode?: string;
  Quantity: number;
  ShrinkagePercent: number;
}

interface ProductCategory {
  Id: number;
  Name: string;
  IsActive: boolean;
  IsForSale: boolean;
}

interface ProductPicture {
  Id: number;
  Picture: string;
}

interface Product {
  Id: number;
  Name: string;
  IsActive: boolean;
  IsDecimal: boolean;
  Price: number;
  Barcode?: string;
  Code?: string;
  Description?: string;
  HotKeyDesktop?: boolean;
  CategoryId?: number;
  LogoPath?: string;
  Logo?: string;
  SetMenus?: SetMenuLink[];
  HappyHours?: HappyHour[];
  ModifierGroups?: ModifierGroup[];
  ProductPictures?: ProductPicture[];
  Components?: ProductComponent[];
  ProductCategory?: ProductCategory;
  SalesOptions?: string[];
  IsPercent?: boolean;
  IsForSale?: boolean;
  IsModifierOnly?: boolean;
  UnitMeasureName?: string;
  TaxPercent?: number;
  HasStock?: boolean;
}

const fmt = (n: number) =>
  new Intl.NumberFormat("sr-RS", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n || 0) + " RSD";

// Assumes JS's Sunday-first convention (0=Nedelja) since Octopus doesn't
// document the DayOfWeek origin — adjust here if that turns out to be wrong.
const DAY_NAMES = ["Nedelja", "Ponedeljak", "Utorak", "Sreda", "Četvrtak", "Petak", "Subota"];

const TAG_COLORS = [
  "magenta", "red", "volcano", "orange", "gold",
  "lime", "green", "cyan", "blue", "geekblue", "purple",
];
const colorForString = (s: string) => {
  let hash = 0;
  for (let i = 0; i < s.length; i++) hash = s.charCodeAt(i) + ((hash << 5) - hash);
  return TAG_COLORS[Math.abs(hash) % TAG_COLORS.length];
};

const normalize = (s: string) =>
  (s || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .trim();

const getProductImage = (p: Product): string | undefined => {
  if (p.LogoPath && /^https?:\/\//i.test(p.LogoPath)) return p.LogoPath;
  if (p.Logo) return `data:image/png;base64,${p.Logo}`;
  return undefined;
};

const THREE_STATE_OPTIONS = [
  { value: "", label: "Svi" },
  { value: "true", label: "Da" },
  { value: "false", label: "Ne" },
];

const OctopusProducts: React.FC = () => {
  const { t } = useTranslation();
  const userData = useSelector((state: any) => state.userData.user);
  const rawClientId = userData?.clientId;
  const clientId =
    typeof rawClientId === "object" ? rawClientId?._id : rawClientId;

  const { hasPermission } = usePermissions();
  const canView =
    isSuperAdmin(userData?.roles) || hasPermission("octopus-products:view");

  const [tokens, setTokens] = useState<TokenOption[]>([]);
  const [selectedTokenId, setSelectedTokenId] = useState<string | undefined>();
  const [tokensLoading, setTokensLoading] = useState(false);

  // Server-side query filters (sent to Octopus on "Pretraži")
  const [keyword, setKeyword] = useState("");
  const [activeFilter, setActiveFilter] = useState("true");
  const [forSaleFilter, setForSaleFilter] = useState("");
  const [hotKeysFilter, setHotKeysFilter] = useState("");
  const [webFilter, setWebFilter] = useState("");
  const [tags, setTags] = useState<string[]>([]);
  const [lastUpdateFrom, setLastUpdateFrom] = useState<Dayjs | null>(null);
  const [categoryIdInput, setCategoryIdInput] = useState<number | undefined>();

  const [categoryList, setCategoryList] = useState<{ Id: number; Name: string }[]>([]);
  const [categoryListLoading, setCategoryListLoading] = useState(false);

  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [searchText, setSearchText] = useState("");

  // Controlled table filter/sort state — drives the "Reset filters" button and
  // keeps every column filterable/sortable through one consistent mechanism.
  const [filteredInfo, setFilteredInfo] = useState<Record<string, any>>({});
  const [sortedInfo, setSortedInfo] = useState<any>({ columnKey: "Name", order: "ascend" });

  useEffect(() => {
    if (!clientId || !canView) return;
    setTokensLoading(true);
    axios
      .get(`${BASE_API_URL}/${clientId}/octopus-tokens`)
      .then((res) => {
        setTokens(res.data || []);
        if (res.data?.length === 1) setSelectedTokenId(res.data[0]._id);
      })
      .catch(() => {})
      .finally(() => setTokensLoading(false));
  }, [clientId]);

  useEffect(() => {
    if (!clientId || !canView || !selectedTokenId) return;
    const token = tokens.find((tk) => tk._id === selectedTokenId);
    setCategoryListLoading(true);
    axios
      .get(`${BASE_API_URL}/${clientId}/octopus-tokens/product-categories`, {
        params: token?.locationId?._id ? { locationId: token.locationId._id } : {},
      })
      .then((res) => setCategoryList(res.data || []))
      .catch(() => setCategoryList([]))
      .finally(() => setCategoryListLoading(false));
  }, [clientId, selectedTokenId]);

  const handleSearch = async () => {
    setLoading(true);
    setSearched(true);
    setFilteredInfo({});
    setSortedInfo({ columnKey: "Name", order: "ascend" });
    try {
      const params: Record<string, string> = {};
      if (keyword.trim()) params.keyword = keyword.trim();
      if (activeFilter) params.active = activeFilter;
      if (forSaleFilter) params.isForSale = forSaleFilter;
      if (hotKeysFilter) params.hotKeys = hotKeysFilter;
      if (webFilter) params.isWeb = webFilter;
      if (lastUpdateFrom) params.lastUpdateFrom = lastUpdateFrom.format("YYYY-MM-DDTHH:mm:ss");
      if (categoryIdInput) params.categoryId = String(categoryIdInput);

      const token = tokens.find((tk) => tk._id === selectedTokenId);
      if (token?.locationId?._id) params.locationId = token.locationId._id;

      const res = await axios.get(`${BASE_API_URL}/${clientId}/octopus-tokens/products`, {
        params,
        paramsSerializer: (p) => {
          const qs = new URLSearchParams();
          Object.entries(p).forEach(([k, v]) => qs.append(k, String(v)));
          tags.forEach((tg) => qs.append("tag", tg));
          return qs.toString();
        },
      });
      setProducts(res.data || []);
    } catch (err: any) {
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  const resetFilters = () => {
    setFilteredInfo({});
    setSortedInfo({});
    setSearchText("");
  };

  const searchedProducts = useMemo(() => {
    if (!searchText.trim()) return products;
    const q = normalize(searchText);
    return products.filter(
      (p) =>
        normalize(p.Name).includes(q) ||
        normalize(p.Code || "").includes(q) ||
        normalize(p.Barcode || "").includes(q) ||
        normalize(p.Description || "").includes(q) ||
        normalize(p.ProductCategory?.Name || "").includes(q)
    );
  }, [products, searchText]);

  // Filter option lists are derived from the full loaded result set (not the
  // currently filtered rows), so picking one filter never hides the others.
  const categoryFilters = useMemo(() => {
    const names = Array.from(
      new Set(products.map((p) => p.ProductCategory?.Name).filter(Boolean) as string[])
    );
    return names.sort((a, b) => a.localeCompare(b, "sr")).map((n) => ({ text: n, value: n }));
  }, [products]);

  const unitFilters = useMemo(() => {
    const names = Array.from(new Set(products.map((p) => p.UnitMeasureName).filter(Boolean) as string[]));
    return names.sort((a, b) => a.localeCompare(b, "sr")).map((n) => ({ text: n, value: n }));
  }, [products]);

  const salesChannelFilters = useMemo(() => {
    const names = Array.from(new Set(products.flatMap((p) => p.SalesOptions || [])));
    return names.sort((a, b) => a.localeCompare(b, "sr")).map((n) => ({ text: n, value: n }));
  }, [products]);

  const categoryOptions = useMemo(
    () => categoryFilters.map((f) => ({ label: f.text, value: f.value })),
    [categoryFilters]
  );
  const unitOptions = useMemo(
    () => unitFilters.map((f) => ({ label: f.text, value: f.value })),
    [unitFilters]
  );
  const salesChannelOptions = useMemo(
    () => salesChannelFilters.map((f) => ({ label: f.text, value: f.value })),
    [salesChannelFilters]
  );

  const setColumnFilter = (key: string, value: any[]) => {
    setFilteredInfo((prev) => ({ ...prev, [key]: value.length ? value : null }));
  };

  // Applies every active column filter on top of the free-text search, so the
  // table rows and the summary totals below are always in sync with each other.
  const displayedProducts = useMemo(() => {
    let result = searchedProducts;
    const categoryVals: string[] | null = filteredInfo.ProductCategory || null;
    if (categoryVals?.length)
      result = result.filter((p) => categoryVals.includes(p.ProductCategory?.Name || ""));
    const unitVals: string[] | null = filteredInfo.UnitMeasureName || null;
    if (unitVals?.length) result = result.filter((p) => unitVals.includes(p.UnitMeasureName || ""));
    const channelVals: string[] | null = filteredInfo.SalesOptions || null;
    if (channelVals?.length)
      result = result.filter((p) => (p.SalesOptions || []).some((c) => channelVals.includes(c)));
    const statusVals: string[] | null = filteredInfo.IsActive || null;
    if (statusVals?.length)
      result = result.filter((p) => statusVals.includes(p.IsActive ? "active" : "inactive"));
    const priceVal: string[] | null = filteredInfo.Price || null;
    if (priceVal?.length) {
      const [minStr, maxStr] = String(priceVal[0]).split(",");
      const min = minStr ? Number(minStr) : -Infinity;
      const max = maxStr ? Number(maxStr) : Infinity;
      result = result.filter((p) => p.Price >= min && p.Price <= max);
    }
    return result;
  }, [searchedProducts, filteredInfo]);

  const activeCount = displayedProducts.filter((p) => p.IsActive).length;
  const forSaleCount = displayedProducts.filter((p) => p.IsForSale).length;
  const hotKeyCount = displayedProducts.filter((p) => p.HotKeyDesktop).length;

  const hasActiveFilters =
    Object.values(filteredInfo).some((v) => (Array.isArray(v) ? v.length > 0 : v != null)) ||
    !!searchText.trim();

  if (!canView) {
    return (
      <Result
        status="403"
        title="403"
        subTitle={t("You don't have permission to view this page.")}
      />
    );
  }

  const priceFilterDropdown = ({
    setSelectedKeys,
    selectedKeys,
    confirm,
    clearFilters,
  }: FilterDropdownProps) => {
    const [minStr, maxStr] = (selectedKeys[0] as string | undefined)?.split(",") || ["", ""];
    return (
      <div style={{ padding: 8 }}>
        <Space>
          <InputNumber
            placeholder="Min"
            value={minStr ? Number(minStr) : undefined}
            onChange={(v) => setSelectedKeys([`${v ?? ""},${maxStr ?? ""}`])}
            style={{ width: 100 }}
          />
          <InputNumber
            placeholder="Max"
            value={maxStr ? Number(maxStr) : undefined}
            onChange={(v) => setSelectedKeys([`${minStr ?? ""},${v ?? ""}`])}
            style={{ width: 100 }}
          />
        </Space>
        <div style={{ marginTop: 8, display: "flex", justifyContent: "space-between" }}>
          <Button
            size="small"
            onClick={() => {
              clearFilters?.();
              setSelectedKeys([]);
              confirm();
            }}
          >
            Reset
          </Button>
          <Button size="small" type="primary" onClick={() => confirm()}>
            OK
          </Button>
        </div>
      </div>
    );
  };

  const columns = [
    {
      title: "Proizvod",
      dataIndex: "Name",
      key: "Name",
      sorter: (a: Product, b: Product) => (a.Name || "").localeCompare(b.Name || "", "sr"),
      sortOrder: sortedInfo.columnKey === "Name" ? sortedInfo.order : null,
      render: (name: string, row: Product) => {
        const img = getProductImage(row);
        return (
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            {img ? (
              <img
                src={img}
                alt={name}
                style={{ width: 36, height: 36, objectFit: "cover", borderRadius: 6, flexShrink: 0 }}
                onError={(e) => {
                  (e.currentTarget as HTMLImageElement).style.display = "none";
                }}
              />
            ) : (
              <div
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: 6,
                  background: "#f3f4f6",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <ShoppingOutlined style={{ color: "#9ca3af" }} />
              </div>
            )}
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 600, fontSize: 13, display: "flex", alignItems: "center", gap: 6 }}>
                {name}
                {row.HotKeyDesktop && (
                  <Tooltip title="Hot key">
                    <StarFilled style={{ color: "#f59e0b", fontSize: 12 }} />
                  </Tooltip>
                )}
              </div>
              <div style={{ fontSize: 11, color: "#9ca3af" }}>
                {[row.Code, row.Barcode].filter(Boolean).join(" · ") || "—"}
              </div>
            </div>
          </div>
        );
      },
    },
    {
      title: "Kategorija",
      dataIndex: "ProductCategory",
      key: "ProductCategory",
      sorter: (a: Product, b: Product) =>
        (a.ProductCategory?.Name || "").localeCompare(b.ProductCategory?.Name || "", "sr"),
      sortOrder: sortedInfo.columnKey === "ProductCategory" ? sortedInfo.order : null,
      filters: categoryFilters,
      filteredValue: filteredInfo.ProductCategory || null,
      filterSearch: true,
      onFilter: (value: any, record: Product) => record.ProductCategory?.Name === value,
      render: (cat: ProductCategory | undefined) =>
        cat?.Name ? <Tag color={colorForString(cat.Name)}>{cat.Name}</Tag> : "—",
    },
    {
      title: "Cena",
      dataIndex: "Price",
      key: "Price",
      align: "right" as const,
      sorter: (a: Product, b: Product) => a.Price - b.Price,
      sortOrder: sortedInfo.columnKey === "Price" ? sortedInfo.order : null,
      filteredValue: filteredInfo.Price || null,
      filterIcon: (filtered: boolean) => (
        <FilterOutlined style={{ color: filtered ? "#1677ff" : undefined }} />
      ),
      filterDropdown: priceFilterDropdown,
      onFilter: (value: any, record: Product) => {
        const [minStr, maxStr] = String(value).split(",");
        const min = minStr ? Number(minStr) : -Infinity;
        const max = maxStr ? Number(maxStr) : Infinity;
        return record.Price >= min && record.Price <= max;
      },
      render: (v: number, row: Product) => (
        <strong>{row.IsPercent ? `${v}%` : fmt(v)}</strong>
      ),
    },
    {
      title: "PDV %",
      dataIndex: "TaxPercent",
      key: "TaxPercent",
      align: "right" as const,
      sorter: (a: Product, b: Product) => (a.TaxPercent || 0) - (b.TaxPercent || 0),
      sortOrder: sortedInfo.columnKey === "TaxPercent" ? sortedInfo.order : null,
      render: (v: number) => (v || v === 0 ? `${v}%` : "—"),
    },
    {
      title: "Jedinica",
      dataIndex: "UnitMeasureName",
      key: "UnitMeasureName",
      sorter: (a: Product, b: Product) =>
        (a.UnitMeasureName || "").localeCompare(b.UnitMeasureName || "", "sr"),
      sortOrder: sortedInfo.columnKey === "UnitMeasureName" ? sortedInfo.order : null,
      filters: unitFilters,
      filteredValue: filteredInfo.UnitMeasureName || null,
      onFilter: (value: any, record: Product) => record.UnitMeasureName === value,
      render: (v: string) => v || "—",
    },
    {
      title: "Prodajni kanali",
      dataIndex: "SalesOptions",
      key: "SalesOptions",
      filters: salesChannelFilters,
      filteredValue: filteredInfo.SalesOptions || null,
      onFilter: (value: any, record: Product) => (record.SalesOptions || []).includes(value),
      render: (opts: string[]) => (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
          {(opts || []).map((o) => (
            <Tag key={o}>{o}</Tag>
          ))}
        </div>
      ),
    },
    {
      title: "Status",
      dataIndex: "IsActive",
      key: "IsActive",
      sorter: (a: Product, b: Product) => Number(a.IsActive) - Number(b.IsActive),
      sortOrder: sortedInfo.columnKey === "IsActive" ? sortedInfo.order : null,
      filters: [
        { text: "Aktivan", value: "active" },
        { text: "Neaktivan", value: "inactive" },
      ],
      filteredValue: filteredInfo.IsActive || null,
      onFilter: (value: any, record: Product) =>
        value === "active" ? record.IsActive : !record.IsActive,
      render: (_: boolean, row: Product) => (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
          <Tag color={row.IsActive ? "green" : "default"}>
            {row.IsActive ? "Aktivan" : "Neaktivan"}
          </Tag>
          {row.IsForSale && <Tag color="blue">Za prodaju</Tag>}
          {row.IsModifierOnly && <Tag color="purple">Modifikator</Tag>}
          {row.HasStock && <Tag color="cyan">Na stanju</Tag>}
        </div>
      ),
    },
  ];

  return (
    <div style={{ padding: "24px 24px 0" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          marginBottom: 24,
          flexWrap: "wrap",
        }}
      >
        <h1 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: "#111827" }}>
          Proizvodi
        </h1>
      </div>

      {/* Search filters (sent to Octopus) */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          marginBottom: 12,
          flexWrap: "wrap",
          background: "#f9fafb",
          border: "1px solid #e5e7eb",
          borderRadius: 10,
          padding: "12px 16px",
        }}
      >
        <Select
          placeholder="Izaberite lokaciju"
          value={selectedTokenId}
          onChange={setSelectedTokenId}
          loading={tokensLoading}
          style={{ minWidth: 220 }}
          optionLabelProp="label"
          options={tokens.map((tk) => ({
            value: tk._id,
            label: tk.name,
            desc: tk.locationId?.name,
          }))}
          optionRender={(opt) => (
            <div>
              <div style={{ fontWeight: 600 }}>{opt.data.label}</div>
              {opt.data.desc && (
                <div style={{ fontSize: 11, color: "#9ca3af" }}>{opt.data.desc}</div>
              )}
            </div>
          )}
          notFoundContent={
            tokensLoading
              ? "Učitavanje..."
              : "Nema podešenih lokacija. Dodajte ih u Podešavanja → Octopus."
          }
        />

        <Input
          placeholder="Pretraga po nazivu proizvoda..."
          prefix={<SearchOutlined style={{ color: "#9ca3af" }} />}
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
          allowClear
          style={{ minWidth: 220 }}
          onPressEnter={handleSearch}
        />

        <Space size={4}>
          <span style={{ fontSize: 12, color: "#6b7280" }}>Aktivan:</span>
          <Select
            value={activeFilter}
            onChange={setActiveFilter}
            options={THREE_STATE_OPTIONS}
            style={{ width: 90 }}
          />
        </Space>

        <Space size={4}>
          <span style={{ fontSize: 12, color: "#6b7280" }}>Za prodaju:</span>
          <Select
            value={forSaleFilter}
            onChange={setForSaleFilter}
            options={THREE_STATE_OPTIONS}
            style={{ width: 90 }}
          />
        </Space>

        <Space size={4}>
          <span style={{ fontSize: 12, color: "#6b7280" }}>Hot key:</span>
          <Select
            value={hotKeysFilter}
            onChange={setHotKeysFilter}
            options={THREE_STATE_OPTIONS}
            style={{ width: 90 }}
          />
        </Space>

        <Space size={4}>
          <span style={{ fontSize: 12, color: "#6b7280" }}>Web:</span>
          <Select
            value={webFilter}
            onChange={setWebFilter}
            options={THREE_STATE_OPTIONS}
            style={{ width: 90 }}
          />
        </Space>

        <Select
          mode="tags"
          placeholder="Tagovi (Enter za dodavanje)"
          value={tags}
          onChange={setTags}
          style={{ minWidth: 200 }}
          tokenSeparators={[","]}
        />

        <DatePicker
          showTime
          placeholder="Ažurirano od"
          value={lastUpdateFrom}
          onChange={setLastUpdateFrom}
          format="DD.MM.YYYY HH:mm"
        />

        <Select
          showSearch
          allowClear
          placeholder="Kategorija"
          value={categoryIdInput}
          onChange={(v) => setCategoryIdInput(v)}
          loading={categoryListLoading}
          filterOption={(input, opt) =>
            (opt?.label as string)?.toLowerCase().includes(input.toLowerCase())
          }
          options={categoryList.map((c) => ({ value: c.Id, label: c.Name }))}
          style={{ minWidth: 180 }}
          notFoundContent={categoryListLoading ? "Učitavanje..." : "Nema kategorija"}
        />

        <Button
          type="primary"
          icon={<SearchOutlined />}
          onClick={handleSearch}
          loading={loading}
          disabled={!selectedTokenId}
        >
          Pretraži
        </Button>
      </div>

      {/* Advanced filters — extracted directly from the loaded products, so every
          category/unit/sales channel that actually appears can be picked without
          needing to know it in advance. */}
      {products.length > 0 && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            marginBottom: 20,
            flexWrap: "wrap",
          }}
        >
          <Input
            placeholder="Pretraži po nazivu, šifri, barkodu, opisu..."
            prefix={<SearchOutlined style={{ color: "#9ca3af" }} />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            allowClear
            style={{ minWidth: 260 }}
          />
          <Select
            mode="multiple"
            allowClear
            placeholder="Filtriraj po kategoriji"
            value={filteredInfo.ProductCategory || []}
            onChange={(vals) => setColumnFilter("ProductCategory", vals)}
            options={categoryOptions}
            maxTagCount="responsive"
            style={{ minWidth: 220 }}
          />
          <Select
            mode="multiple"
            allowClear
            placeholder="Filtriraj po jedinici mere"
            value={filteredInfo.UnitMeasureName || []}
            onChange={(vals) => setColumnFilter("UnitMeasureName", vals)}
            options={unitOptions}
            maxTagCount="responsive"
            style={{ minWidth: 200 }}
          />
          <Select
            mode="multiple"
            allowClear
            placeholder="Filtriraj po kanalu prodaje"
            value={filteredInfo.SalesOptions || []}
            onChange={(vals) => setColumnFilter("SalesOptions", vals)}
            options={salesChannelOptions}
            maxTagCount="responsive"
            style={{ minWidth: 200 }}
          />
          <InputNumber
            placeholder="Min cena"
            value={
              filteredInfo.Price?.[0] ? Number(String(filteredInfo.Price[0]).split(",")[0]) || undefined : undefined
            }
            onChange={(v) => {
              const max = filteredInfo.Price?.[0]?.split(",")[1] || "";
              setColumnFilter("Price", v == null && !max ? [] : [`${v ?? ""},${max}`]);
            }}
            style={{ width: 110 }}
          />
          <InputNumber
            placeholder="Max cena"
            value={
              filteredInfo.Price?.[0] ? Number(String(filteredInfo.Price[0]).split(",")[1]) || undefined : undefined
            }
            onChange={(v) => {
              const min = filteredInfo.Price?.[0]?.split(",")[0] || "";
              setColumnFilter("Price", v == null && !min ? [] : [`${min},${v ?? ""}`]);
            }}
            style={{ width: 110 }}
          />

          {hasActiveFilters && <Button onClick={resetFilters}>Resetuj filtere</Button>}
        </div>
      )}

      {/* Summary row */}
      {searched && !loading && displayedProducts.length > 0 && (
        <div
          style={{
            display: "flex",
            gap: 24,
            marginBottom: 16,
            flexWrap: "wrap",
            fontSize: 13,
            color: "#6b7280",
          }}
        >
          <span>
            Ukupno proizvoda:{" "}
            <strong style={{ color: "#111827" }}>{displayedProducts.length}</strong>
          </span>
          <span>
            Aktivnih: <strong style={{ color: "#111827" }}>{activeCount}</strong>
          </span>
          <span>
            Za prodaju: <strong style={{ color: "#111827" }}>{forSaleCount}</strong>
          </span>
          {hotKeyCount > 0 && (
            <span>
              Hot key: <strong style={{ color: "#111827" }}>{hotKeyCount}</strong>
            </span>
          )}
        </div>
      )}

      <Table
        dataSource={displayedProducts}
        columns={columns}
        rowKey="Id"
        loading={loading}
        pagination={{ pageSize: 20, showSizeChanger: false }}
        rowClassName={(row: Product) => (!row.IsActive ? "octopus-product-inactive-row" : "")}
        onChange={(_pagination, filters, sorter) => {
          setFilteredInfo(filters);
          setSortedInfo(Array.isArray(sorter) ? sorter[0] || {} : sorter);
        }}
        expandable={{
          rowExpandable: (row) =>
            !!row.Description ||
            (row.ModifierGroups || []).length > 0 ||
            (row.Components || []).length > 0 ||
            (row.HappyHours || []).length > 0 ||
            (row.SetMenus || []).length > 0,
          expandedRowRender: (row) => (
            <div style={{ padding: "8px 16px", display: "flex", flexDirection: "column", gap: 16 }}>
              {row.Description && (
                <div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: "#6b7280", marginBottom: 4 }}>
                    Opis
                  </div>
                  <div style={{ fontSize: 13 }}>{row.Description}</div>
                </div>
              )}

              {(row.ModifierGroups || []).length > 0 && (
                <div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: "#6b7280", marginBottom: 4 }}>
                    Modifikatori
                  </div>
                  {row.ModifierGroups!.map((g) => (
                    <div key={g.Id} style={{ marginBottom: 8 }}>
                      <div style={{ fontSize: 12, fontWeight: 500 }}>
                        {g.Title}{" "}
                        <span style={{ color: "#9ca3af" }}>
                          (min {g.Min}, max {g.Max})
                        </span>
                      </div>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 4 }}>
                        {g.Items.map((item) => (
                          <Tag key={item.Id}>
                            {item.ProductName}
                            {item.Price ? ` (+${fmt(item.Price)})` : ""}
                          </Tag>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {(row.Components || []).length > 0 && (
                <div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: "#6b7280", marginBottom: 4 }}>
                    Sastojci / komponente
                  </div>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                    <thead>
                      <tr style={{ color: "#6b7280", textAlign: "left" }}>
                        <th style={{ padding: "4px 8px" }}>Šifra</th>
                        <th style={{ padding: "4px 8px", textAlign: "right" }}>Količina</th>
                        <th style={{ padding: "4px 8px", textAlign: "right" }}>Kalo %</th>
                      </tr>
                    </thead>
                    <tbody>
                      {row.Components!.map((c) => (
                        <tr key={c.Id} style={{ borderTop: "1px solid #e5e7eb" }}>
                          <td style={{ padding: "4px 8px" }}>{c.ChildProductCode || c.ChildProductId}</td>
                          <td style={{ padding: "4px 8px", textAlign: "right" }}>{c.Quantity}</td>
                          <td style={{ padding: "4px 8px", textAlign: "right" }}>{c.ShrinkagePercent}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {(row.HappyHours || []).length > 0 && (
                <div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: "#6b7280", marginBottom: 4 }}>
                    Happy hours
                  </div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                    {row.HappyHours!.map((hh, idx) => (
                      <Tag key={idx} color="gold">
                        {DAY_NAMES[hh.DayOfWeek % 7]} {hh.TimeFrom}–{hh.TimeTo}: {fmt(hh.Price)}
                      </Tag>
                    ))}
                  </div>
                </div>
              )}

              {(row.SetMenus || []).length > 0 && (
                <div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: "#6b7280", marginBottom: 4 }}>
                    Set meniji
                  </div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                    {row.SetMenus!.map((sm, idx) => (
                      <Tag key={idx}>
                        Proizvod #{sm.ProductId}: {fmt(sm.Price)}
                      </Tag>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ),
        }}
        locale={{
          emptyText: searched
            ? "Nema proizvoda za izabrane filtere"
            : "Izaberite lokaciju i kliknite Pretraži",
        }}
        scroll={{ x: 1100 }}
      />

      <style>{`
        .octopus-product-inactive-row td {
          background: #f9fafb !important;
          opacity: 0.75;
        }
      `}</style>
    </div>
  );
};

export default OctopusProducts;
